import { getCore } from '../../../../client/core/getCore';
import type { DynamicCore } from '../../../../client/core/types';
import type { DynamicClient } from '../../../../client/types';
import type { NoncePool } from '../schema';
import { noncePoolStorageKeySchema } from '../schema';

export type UpdateNoncePoolStorageParams = {
  /**
   * Receives the currently stored pool (or null when absent) and returns the
   * pool to persist, or null to leave storage untouched.
   */
  update: (stored: NoncePool | null) => NoncePool | null;
};

// Serializes read-modify-write cycles per core so concurrent updates in the
// same tab cannot interleave and resurrect an already-consumed nonce.
const pendingUpdates = new WeakMap<DynamicCore, Promise<void>>();

/**
 * Updates the persisted nonce pool with a serialized read-modify-write.
 *
 * The write is awaited before resolving, so once this returns the storage
 * ledger reflects the update. Returns the pool as it was read (pre-update).
 * @notInstrumented
 */
export const updateNoncePoolStorage = async (
  { update }: UpdateNoncePoolStorageParams,
  client: DynamicClient
): Promise<NoncePool | null> => {
  const core = getCore(client);

  let storedBeforeUpdate: NoncePool | null = null;

  const run = async () => {
    const stored = await core.storage.getItem(noncePoolStorageKeySchema);

    storedBeforeUpdate = stored;

    const updated = update(stored);

    if (updated !== null) {
      await core.storage.setItem(noncePoolStorageKeySchema, updated);
    }
  };

  // Chain onto the previous update whether it succeeded or failed, so one
  // rejected update does not stall the queue for later callers.
  const chained = (pendingUpdates.get(core) ?? Promise.resolve()).then(
    run,
    run
  );

  pendingUpdates.set(core, chained);

  await chained;

  return storedBeforeUpdate;
};
